/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import {
  type HTMLAttributes,
  type ReactNode,
  useRef,
  type FC,
  type Ref,
} from "react";
import { motion } from "motion/react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import type { BorderProp, RequiredChildrenProp } from "../../props";
import { useClickOutside, useKeys, useUuid } from "../../hooks";
import {
  TargetPositioner,
  type TargetPositionerProps,
} from "../TargetPositioner";
import { zIndexes } from "../../theme";
import { mergeRefs } from "../../utils";
import { FocusTrap, type FocusTrapProps } from "../FocusTrap";

import { elevations } from "../../theme/css/elevations.css";
import { typography } from "../../theme/css/typography.css";
import { popupStyle } from "./Popup.css";

import {
  positionerTransition,
  positionerVariants,
} from "../TargetPositioner/TargetPositioner.motion";
import { borders } from "../../theme/css/borders.css";

export type PopupProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "event" | "definition" | "content"
> &
  Pick<
    TargetPositionerProps,
    | "placement"
    | "active"
    | "click"
    | "focus"
    | "hover"
    | "applyActive"
    | "targetRef"
  > &
  RequiredChildrenProp &
  BorderProp &
  RecipeVariants<typeof popupStyle> & {
    content?: ReactNode;
    escape?: boolean;
    trapFocus?: boolean;
    trapFocusMode?: FocusTrapProps["mode"];
    outsideClose?: boolean;
    width?: string;
    maxWidth?: string;
    minWidth?: string;
    onClose?: () => void;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * Popup component that displays content in a popover.
 */
export const Popup: FC<PopupProps> = ({
  content,
  active = false,
  click,
  focus,
  applyActive,
  hover,
  placement = "top",
  variant,
  escape: escapable,
  size = "default",
  role,
  trapFocus,
  trapFocusMode,
  width,
  targetRef,
  maxWidth,
  minWidth,
  outsideClose,
  border,
  onClose,
  children,
  className,
  ref,
  ...props
}) => {
  const id = useUuid();
  const internalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useKeys("Escape", () => {
    escapable && onClose?.();
  });

  if (outsideClose) {
    // biome-ignore lint/correctness/useHookAtTopLevel: intentional conditional — outsideClose is static per mount
    useClickOutside<HTMLElement>([internalRef, targetRef], () => {
      onClose?.();
    });
  }

  return (
    <TargetPositioner
      id={id}
      targetRef={targetRef}
      placement={placement}
      active={active}
      click={click}
      hover={hover}
      focus={focus}
      applyActive={applyActive}
      zIndex={zIndexes.popup}
      content={
        <FocusTrap active={trapFocus} onEscape={onClose} mode={trapFocusMode}>
          <motion.div
            ref={mergeRefs(internalRef, ref)}
            id={id}
            className={mergeClasses(
              popupStyle({ variant, size }),
              typography.bodyM,
              elevations.l,
              border ? borders[border] : undefined,
              className,
            )}
            style={{ width, maxWidth, minWidth }}
            transition={positionerTransition}
            variants={positionerVariants()[placement]}
            initial="initial"
            animate="animate"
            exit="exit"
            role={role}
            {...(props as any)}
          >
            <div ref={contentRef}>{content}</div>
          </motion.div>
        </FocusTrap>
      }
    >
      {children}
    </TargetPositioner>
  );
};
